<template>
  <div class="search-bar-container">
    <div class="search-form">
      <!-- โครงการ -->
      <div class="form-group">
        <label :class="{ 'error-label': !isFormValid() }">โครงการ *</label>
        <el-select
          :value="searchParam.projectId"
          :clearable="false"
          placeholder="เลือกโครงการ"
          @change="(value) => updateSearchParam('projectId', value)"
        >
          <el-option
            v-for="project in projectOptions"
            :key="project.id"
            :label="project.name"
            :value="project.id"
          />
        </el-select>
      </div>

      <!-- เลขที่ห้อง -->
      <div class="form-group">
        <label>เลขที่ห้อง</label>
        <el-input
          :value="searchParam.addressNo"
          placeholder="ระบุเลขที่ห้อง"
          clearable
          @input="(value) => updateSearchParam('addressNo', value)"
        />
      </div>

      <!-- ชื่อลูกค้า -->
      <div class="form-group">
        <label>ชื่อลูกค้า</label>
        <el-input
          :value="searchParam.firstName"
          placeholder="ระบุชื่อลูกค้า"
          clearable
          @input="(value) => updateSearchParam('firstName', value)"
        />
      </div>

      <!-- นามสกุล -->
      <div class="form-group">
        <label>นามสกุล</label>
        <el-input
          :value="searchParam.lastName"
          placeholder="ระบุนามสกุล"
          clearable
          @input="(value) => updateSearchParam('lastName', value)"
        />
      </div>

      <!-- ช่วงวันที่ต้องการค้นหา -->
      <div class="form-group">
        <label>ช่วงวันที่ต้องการค้นหา</label>
        <el-date-picker
          :value="dateRange"
          type="daterange"
          range-separator="ถึง"
          clearable
          start-placeholder="วันที่เริ่มต้น"
          end-placeholder="วันที่สิ้นสุด"
          @input="handleChangeDate($event)"
        ></el-date-picker>
      </div>

      <!-- Search Button -->
      <div class="form-group search-button-group">
        <sc-button variant="secondary" @click="handleSearch"> ค้นหา </sc-button>
      </div>
    </div>

    <!-- Tab Bar -->
    <div class="tab-bar">
      <div
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab-item', { active: activeTab === tab.key }]"
        @click="setActiveTab(tab.key)"
      >
        {{ tab.label }}
      </div>
    </div>
  </div>
</template>

<script>
// Components
import ScButton from "@/lib/components/ScButton"
import { Input, Select, Option, DatePicker } from "element-ui"

// Utils
import { existValueAndEmptyString, existValue } from "@/lib/util"

// Client
import { backendAdminClient } from "@/dependencies"

// Enums
import {
  virtualSportApprovalStatus,
  virtualSportApprovalStatusLabel,
} from "../config/enums"

export default {
  name: "VirtualSportSearchBar",
  components: {
    ScButton,
    "el-input": Input,
    "el-select": Select,
    "el-option": Option,
    "el-date-picker": DatePicker,
  },
  props: {
    searchParam: {
      type: Object,
      default: () => ({
        projectId: null,
        addressNo: null,
        firstName: null,
        lastName: null,
        startDate: null,
        endDate: null,
        status: virtualSportApprovalStatus.PENDING,
      }),
    },
  },
  data() {
    return {
      activeTab: this.$route.query.status || virtualSportApprovalStatus.PENDING,
      tabs: [
        {
          key: virtualSportApprovalStatus.PENDING,
          label:
            virtualSportApprovalStatusLabel[virtualSportApprovalStatus.PENDING],
        },
        {
          key: virtualSportApprovalStatus.APPROVED,
          label:
            virtualSportApprovalStatusLabel[
              virtualSportApprovalStatus.APPROVED
            ],
        },
        {
          key: virtualSportApprovalStatus.REJECTED,
          label:
            virtualSportApprovalStatusLabel[
              virtualSportApprovalStatus.REJECTED
            ],
        },
      ],
      projectOptions: [],
    }
  },
  computed: {
    dateRange() {
      if (
        existValue(this.searchParam.startDate) &&
        existValue(this.searchParam.endDate)
      ) {
        return [this.searchParam.startDate, this.searchParam.endDate]
      }
      return null
    },
  },
  watch: {
    "searchParam.status": function searchParamStatusWatcher(newStatus) {
      if (newStatus && this.activeTab !== newStatus) {
        this.activeTab = newStatus
      }
    },
    "$route.query.status": function routeQueryStatusWatcher(newStatus) {
      this.activeTab = newStatus || virtualSportApprovalStatus.PENDING
    },
  },
  mounted() {
    this.setUpData()
  },
  methods: {
    isFormValid() {
      return existValueAndEmptyString(this.searchParam.projectId)
    },
    setActiveTab(tabKey) {
      this.activeTab = tabKey
      const updatedParam = {
        ...this.searchParam,
        status: tabKey,
      }
      this.$emit("searchParam", updatedParam)
      this.$emit("tabChange", tabKey)
    },
    handleSearch() {
      if (!this.isFormValid()) {
        this.$message.warning("กรุณาเลือกโครงการ")
        return
      }

      this.$emit("search", this.searchParam)
    },
    handleChangeDate(value) {
      if (!value) {
        const updatedParam = {
          ...this.searchParam,
          startDate: null,
          endDate: null,
        }
        this.$emit("searchParam", updatedParam)
        return
      }

      const [fromJSDate, toJSDate] = value
      const updatedParam = {
        ...this.searchParam,
        startDate: fromJSDate,
        endDate: toJSDate,
      }

      this.$emit("searchParam", updatedParam)
    },
    updateSearchParam(field, value) {
      const updatedParam = {
        ...this.searchParam,
        [field]: value,
      }
      this.$emit("searchParam", updatedParam)
    },
    async setUpData() {
      const results = await this.fetchData()
      this.projectOptions = results

      if (!this.searchParam.projectId) {
        this.updateSearchParam("projectId", results[0].id)
      }
    },
    async fetchData() {
      try {
        const { data } =
          await backendAdminClient.virtualSport.getAdminCobeProject()

        const results = data.result || []

        return results
      } catch (error) {
        return []
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.search-bar-container {
  background: white;
  padding: 20px;
}

.tab-bar {
  display: flex;
  border-bottom: 1px solid #e0e6ed;
  margin-left: -20px;
  margin-right: -20px;
}

.tab-item {
  padding: 15px 24px;
  min-width: 120px;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  text-align: center;
}

.tab-item.active {
  color: $orange;
  border-bottom-color: $orange;
}

.search-form {
  display: flex;
  align-items: end;
  gap: 16px;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.form-group label {
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.search-select,
.search-input {
  width: 100%;
}

.search-button-group {
  min-width: auto;
}

.error-label {
  color: $red !important;
}

/* Responsive */
@media (max-width: 1200px) {
  .form-group {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .search-form {
    flex-direction: column;
  }

  .form-group {
    min-width: 100%;
  }

  .search-button-group {
    width: 100%;
  }

  .search-button {
    width: 100%;
  }
}
</style>
